// What each yellow action card does when played (playing one costs 1 civil action; see Moves PlayActionCard).
// A card either does something by itself, or comes with another action that then costs no civil action of its own:
//   inner: { moves, techTypes? }  the action that comes with the card: the move types allowed (Build, Upgrade, ...)
//                                 and, for builds and upgrades, the kinds of card they can be on
//   discount(card, player)        { kind, amount }: what that action costs less (see Costs.js), used up by it
//   after(player, card, game)     what happens after the action is paid for (e.g. Frugality's food), as log text
//   play(player, card, move, game) what a card without an inner action does, as log text
//   choices(card)                 the options a card offers ({ label, value }); the chosen value is move.choice
// All amounts come from the card's age (see the card classes in this folder).
const ActionNames = require("../Names/ActionNames");
const Actions = require("./Actions");
const TechnologyTypes = require("../TechnologyTypes");
const { MoveTypes } = require("../../Moves/MoveTypes");
const { CostKinds, PoolKinds, TurnFlags } = require("../../Effects/Effects");
const WonderNames = require("../Names/WonderNames");

// Silk Road (completed, or with a stage built): the first action card the player plays on their turn gives 1 more of
// each amount it gives (or takes off); 0 otherwise. On the turn its first stage is built, the first one played after
// that stage (the flags are in the order things happened)
const SilkRoadBonus = (player) => {
    const started = player?.turnFlags.lastIndexOf(TurnFlags.SilkRoadStarted) ?? -1;
    if (!player || player.turnFlags.slice(started + 1).includes(TurnFlags.ActionCardPlayed))
    {
        return 0;
    }
    const built = player.wonders.some(wonder => wonder.name === WonderNames.SilkRoad && !wonder.crumbled)
        || (player.wonderInProgress?.card.name === WonderNames.SilkRoad && player.wonderInProgress.stagesBuilt > 0);
    return built ? 1 : 0;
};
const more = (amount, player) => amount + SilkRoadBonus(player);

const { Farm, Mine } = TechnologyTypes.Production;
const UrbanTypes = Object.values(TechnologyTypes.Urban);

// Civilizations still in the game stronger than / with more culture than the player
const rivalsWithMore = (player, gameState, value) =>
    gameState.playerStates.filter(other => other !== player && !other.resigned && value(other) > value(player)).length;
// The number of players still in the game (the cards' values go by it)
const playersLeft = (gameState) => gameState.playerStates.filter(other => !other.resigned).length;

const ActionEffects = {
    [ActionNames.RichLand]: {
        inner: { moves: [MoveTypes.Build, MoveTypes.Upgrade], techTypes: [Farm, Mine] },
        discount: (card, player) => ({ kind: CostKinds.BuildPayment, amount: more(Actions.RichLand.GetResourceAmount(card.age), player) }),
    },
    [ActionNames.UrbanGrowth]: {
        inner: { moves: [MoveTypes.Build, MoveTypes.Upgrade], techTypes: UrbanTypes },
        discount: (card, player) => ({ kind: CostKinds.BuildPayment, amount: more(Actions.UrbanGrowth.GetResourceAmount(card.age), player) }),
    },
    [ActionNames.EfficientUpgrade]: {
        inner: { moves: [MoveTypes.Upgrade], techTypes: [Farm, Mine, ...UrbanTypes] },
        discount: (card, player) => ({ kind: CostKinds.BuildPayment, amount: more(Actions.EfficientUpgrade.GetResourceAmount(card.age), player) }),
    },
    [ActionNames.EngineeringGenius]: {
        // One stage only, even with a construction technology
        inner: { moves: [MoveTypes.BuildWonderStage], stages: 1 },
        discount: (card, player) => ({ kind: CostKinds.WonderPayment, amount: more(Actions.EngineeringGenius.GetResourceAmount(card.age), player) }),
    },
    [ActionNames.Frugality]: {
        inner: { moves: [MoveTypes.IncreasePopulation] },
        after: (player, card) => {
            const food = more(Actions.Frugality.GetFoodAmount(card.age), player);
            player.GainFood(food);
            return `+${food} food`;
        },
    },
    [ActionNames.Breakthrough]: {
        // Developing a government counts too: peacefully, or by a revolution (paid instead of the card's civil action; see
        // Moves PlayActionCard)
        inner: { moves: [MoveTypes.DiscoverTechnology, MoveTypes.ChangeGovernment] },
        after: (player, card) => {
            const science = more(Actions.Breakthrough.GetScienceAmount(card.age), player);
            player.science += science;
            return `+${science} science`;
        },
    },
    [ActionNames.Stockpile]: {
        play: (player) => {
            const amount = more(1, player);
            player.GainFood(amount);
            player.GainResources(amount);
            return `+${amount} food, +${amount} resource${amount === 1 ? '' : 's'}`;
        },
    },
    [ActionNames.Reserves]: {
        choices: (card, player) => {
            const amount = more(Actions.Reserves.GetQuantity(card.age), player);
            return [{ label: `${amount} food`, value: 'food' }, { label: `${amount} resources`, value: 'resources' }];
        },
        play: (player, card, move) => {
            const amount = more(Actions.Reserves.GetQuantity(card.age), player);
            if (move.choice === 'food')
            {
                player.GainFood(amount);
                return `+${amount} food`;
            }
            player.GainResources(amount);
            return `+${amount} resources`;
        },
    },
    [ActionNames.CulturalHeritage]: {
        play: (player, card) => {
            const science = more(Actions.CulturalHeritage.GetScienceAmount(card.age), player);
            const culture = more(Actions.CulturalHeritage.GetCultureAmount(card.age), player);
            player.science += science;
            player.culture += culture;
            return `+${science} science, +${culture} culture`;
        },
    },
    [ActionNames.RevolutionaryIdea]: {
        play: (player, card) => {
            const science = more(Actions.RevolutionaryIdea.GetScienceAmount(card.age), player);
            player.science += science;
            return `+${science} science`;
        },
    },
    [ActionNames.EndowmentForTheArts]: {
        // Culture for each civilization with more culture, by the number of players
        play: (player, card, move, gameState) => {
            const each = { 2: 6, 3: 3, 4: 2 }[playersLeft(gameState)] ?? 2;
            const rivals = rivalsWithMore(player, gameState, p => p.culture);
            const culture = more(each * rivals, player);
            player.culture += culture;
            return `+${culture} culture (${rivals} with more culture)`;
        },
    },
    [ActionNames.Patriotism]: {
        // This turn only: resources for military units, and a military action
        play: (player, card) => {
            // (Silk Road: 1 more of each, the military actions too)
            const resources = more(Actions.Patriotism.GetResourceAmount(card.age), player);
            const actions = more(1, player);
            player.turnPools.push({ source: card.name, kind: PoolKinds.MilitaryResources, amount: resources });
            player.turnBonuses.push({ source: card.name, stat: 'militaryActions', amount: actions });
            return `this turn: ${resources} resources for military units, +${actions} military action${actions === 1 ? '' : 's'}`;
        },
    },
    [ActionNames.MilitaryBuildup]: {
        // This turn only: resources for military units for each stronger civilization, by the number of players
        play: (player, card, move, gameState) => {
            const each = { 2: Actions.MilitaryBuildup.GetTwoPlayerValue, 3: Actions.MilitaryBuildup.GetThreePlayerValue }[playersLeft(gameState)]
                ?? Actions.MilitaryBuildup.GetFourPlayerValue;
            const rivals = rivalsWithMore(player, gameState, p => p.GetMilitaryStrength());
            const resources = more(each(card.age) * rivals, player);
            if (resources > 0)
            {
                player.turnPools.push({ source: card.name, kind: PoolKinds.MilitaryResources, amount: resources });
            }
            return `this turn: ${resources} resources for military units (${rivals} stronger)`;
        },
    },
};

module.exports = ActionEffects;
module.exports.SilkRoadBonus = SilkRoadBonus;
